import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { HoursPerWeek } from 'api/StudentLoanApi';
import FormNavigation from 'components/FormNavigation';
import RadioButtonList from 'components/Common/RadioButtonList/RadioButtonList';
import NumberInput from 'components/NumberInput';
import Button from 'components/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import useSaveStudentLoanAnswers, { reportStudentLoanSaveFailure } from 'hooks/useSaveStudentLoanAnswers';
import { FlowComponentType } from 'routes/types';
import { getStudentLoanData, isEmployerPossiblyPslfQualifying } from 'selectors/getStudentLoanData';

import QuestionsWrapper from './Wrapper/QuestionsWrapper';

import styles from './Questions.module.scss';

const MIN_START_YEAR = 1950;
const YEAR_LENGTH = 4;

const hoursOptions = [
  { id: HoursPerWeek.ThirtyPlus, label: '30 or more' },
  { id: HoursPerWeek.Under30, label: 'Under 30' },
  { id: HoursPerWeek.Varies, label: 'It varies (per diem or PRN)' },
];

const Work = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const studentLoanData = useSelector(getStudentLoanData);
  const { answers } = studentLoanData;
  const { saveAnswers, isSaving } = useSaveStudentLoanAnswers();

  const [hours, setHours] = useState<HoursPerWeek | null>(answers.hoursPerWeek ?? null);
  const [startYear, setStartYear] = useState(answers.nonprofitStartYear ? String(answers.nonprofitStartYear) : '');

  const asksStartYear = isEmployerPossiblyPslfQualifying(studentLoanData) && hours !== HoursPerWeek.Under30;
  const parsedYear = startYear ? Number(startYear) : null;
  const isYearInvalid =
    asksStartYear &&
    parsedYear !== null &&
    (startYear.length < YEAR_LENGTH || parsedYear < MIN_START_YEAR || parsedYear > new Date().getFullYear());

  const handleClick = async () => {
    analytics.track('Student Loan Work Selected', { hours });
    try {
      handleNext(
        await saveAnswers({
          hoursPerWeek: hours!,
          nonprofitStartYear: asksStartYear ? parsedYear : null,
        }),
      );
    } catch {
      reportStudentLoanSaveFailure();
    }
  };

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <QuestionsWrapper
          title="Tell us about your work"
          subtitle="PSLF counts full-time work for nonprofit and government employers."
        >
          <>
            <RadioButtonList
              label="How many hours do you work in a typical week?"
              selected={hours}
              setSelected={setHours}
              options={hoursOptions}
            />

            {asksStartYear && (
              <div className={styles.question}>
                <NumberInput
                  label="What year did you start working full-time for nonprofit or government employers?"
                  placeholder="For example, 2019"
                  name="nonprofitStartYear"
                  allowNegative={false}
                  maxLength={YEAR_LENGTH}
                  value={startYear}
                  onChange={(event) => setStartYear(event.target.value)}
                  errorMessage={isYearInvalid ? `Enter a year between ${MIN_START_YEAR} and this year.` : undefined}
                />
                <p className={styles.fieldHint}>
                  An estimate is fine, and it doesn&apos;t have to be your current employer.
                </p>
              </div>
            )}

            <Button disabled={!hours || isYearInvalid} isLoading={isSaving} onClick={handleClick}>
              Next
            </Button>
          </>
        </QuestionsWrapper>
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default Work;
